// SupportDetailPage.js (create in ./pages/public/support/SupportDetailPage.js)
import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, SearchIcon } from '../../public/icons';
import { getSupportData } from '../../data/supportdata';

const SupportDetailPage = () => {
  const { slug } = useParams(); // Get the support category from URL
  const [supportData, setSupportData] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  // Load support data based on slug
  useEffect(() => {
    setLoading(true);
    const data = getSupportData(slug);
    setSupportData(data);
    setExpandedIndex(0); // Reset to first FAQ
    setSearchQuery(''); // Clear search
    setLoading(false);
  }, [slug]);

  const toggleFAQ = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  // Filter FAQs based on search
  const filteredFAQs = supportData && searchQuery
    ? supportData.faqs.filter(faq => 
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : supportData?.faqs || [];

  if (loading || !supportData) {
    return (
      <div className="min-h-screen bg-gray-50 py-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D2A146] mx-auto"></div>
          <p className="mt-4 text-[#6B635D]">Loading support information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen max-w-3xl">
      <div className="">
        {/* Back Button */}
        <Link 
          to="/account/support" 
          className="inline-flex items-center text-[#6B635D] hover:text-[#D2A146] mb-8 transition-colors duration-200 group"
        >
          <svg 
            className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform duration-200" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to all topics
        </Link>

        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-[#0E1628]">{supportData.title}</h1>
              <p className="text-lg text-[#6B635D] mt-2">{supportData.description}</p>
            </div>
          </div>

          {/* Search within this topic */}
          <div className="mt-6 relative">
            <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
              <SearchIcon className="w-5 h-5 text-[#6B635D]" />
            </div>
            <input
              type="text"
              placeholder={supportData.searchPlaceholder || "Search within this topic..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-transparent  border border-[#DBDBDB]  focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent text-[#0E1628] placeholder:text-[#6B635D]"
            />
          </div>
        </div>

        {/* Main Content */}
        <div className=" border-b-2 border-[#E4E4E4] overflow-hidden">
          {/* FAQs Section */}
          <div className="divide-y divide-[#E4E4E4]">
            {filteredFAQs.length > 0 ? (
              filteredFAQs.map((faq, index) => (
                <div key={index} className=" pb-6 pt-6 transition-colors duration-200">
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full flex items-center justify-between text-left focus:outline-none"
                  >
                    <h3 className="text-lg font-semibold text-[#0E1628] pr-8">
                      {faq.question}
                    </h3>
                    {expandedIndex === index ? (
                      <ArrowUp className="w-5 h-5 text-[#55000C] flex-shrink-0" />
                    ) : (
                      <ArrowDown className="w-5 h-5 text-[#55000C] flex-shrink-0" />
                    )}
                  </button>
                  
                  {expandedIndex === index && (
                    <div className="mt-4">
                      <div className="text-[#6B635D] leading-relaxed">
                        {faq.answer}
                      </div>
                      
                      {/* Additional Info if exists */}
                      {faq.additionalInfo && (
                        <div className="mt-4 p-4 bg-[#FAFAFA] rounded-lg border border-[#E4E4E4]">
                          <p className="text-sm text-[#6B635D]">{faq.additionalInfo}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="p-8 text-center">
                <p className="text-[#6B635D]">No results found for "{searchQuery}"</p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-2 text-[#D2A146] hover:underline"
                >
                  Clear search
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Related Articles (if any) */}
        {supportData.relatedArticles && supportData.relatedArticles.length > 0 && (
          <div className="mt-8">
            <h3 className="text-xl font-semibold text-[#0E1628] mb-4">Related Articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {supportData.relatedArticles.map((article, index) => (
                <Link
                  key={index}
                  to={article.link}
                  className="block p-4 bg-white rounded-lg border border-[#E4E4E4] hover:border-[#D2A146] hover:shadow-sm transition-all duration-200"
                >
                  <h4 className="font-medium text-[#0E1628] mb-1">{article.title}</h4>
                  <p className="text-sm text-[#6B635D]">{article.description}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Still Need Help Section */}
        <div className=" mt-20">
          <div className="">
            <div className="flex items-center justify-between">
             <div>
                 <h3 className="text-2xl font-bold text-[#0E1628] mb-3">Need Support? </h3>
              <p className="text-[#6B635D] mb-4">
         Can’t find the answer you’re looking for, don’t worry we’re here to help!
              </p>
             </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="mailto:support@kapta.com"
                  className="inline-flex items-center justify-center px-6 py-3 bg-[#D2A146] text-white font-medium hover:bg-[#C19564] transition-colors duration-200"
                >
                  Contact support
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportDetailPage;